/**
 * EncounterTable
 *
 * Desktop table view for displaying encounters.
 * Enterprise-grade design with refined visual hierarchy,
 * dot status indicators, skeleton loading, and improved scannability.
 */

import React from 'react';
import { format } from 'date-fns';
import { EHR_PRE_LOCALIZED_SYSTEMS, formatEhrTime, formatEhrDate } from '@/utils/dateFormat';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  PaperAirplaneIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useEncounterList } from '../EncounterListContext';
import { UnifiedEncounterItem, ENCOUNTER_STATUS_STYLES } from '../types';

// ============================================================================
// Status Configuration
// ============================================================================

const STATUS_ICONS = { sent: PaperAirplaneIcon, alert: ExclamationCircleIcon };

const MATCH_STATUS_STYLES: Record<string, { text: string; bg: string; Icon: typeof CheckCircleIcon }> = {
  matched: { text: 'text-emerald-700', bg: 'bg-emerald-50', Icon: CheckCircleIcon },
  unmatched: { text: 'text-red-700', bg: 'bg-red-50', Icon: ExclamationTriangleIcon },
  pending: { text: 'text-amber-700', bg: 'bg-amber-50', Icon: ClockIcon },
};

// ============================================================================
// Status Badge Component
// ============================================================================

interface StatusBadgeProps {
  status: string;
  type: 'recording' | 'transcription' | 'encounter' | 'match';
}

function StatusBadge({ status, type }: StatusBadgeProps) {
  if (type === 'match') {
    const config = MATCH_STATUS_STYLES[status];
    if (!config) return null;
    const { Icon, text, bg } = config;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
        <Icon className="h-3.5 w-3.5" />
        {status.replace(/-/g, ' ')}
      </span>
    );
  }

  const config = ENCOUNTER_STATUS_STYLES[status] || ENCOUNTER_STATUS_STYLES.pending;
  const Icon = config.icon && STATUS_ICONS[config.icon];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${config.bg} ${config.text}`}>
      {Icon ? (
        <Icon className="h-3.5 w-3.5" />
      ) : (
        <span className={`w-2 h-2 rounded-full ${config.dot} ${config.pulse ? 'animate-pulse' : ''}`} />
      )}
      {config.label}
    </span>
  );
}

// ============================================================================
// Skeleton Loading Row
// ============================================================================

function SkeletonRow({ colSpan }: { colSpan: number }) {
  return (
    <tr className="animate-pulse">
      <td className="px-6 py-5">
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-36" />
          <div className="h-3 bg-gray-100 rounded w-28" />
        </div>
      </td>
      <td className="px-6 py-5">
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-24" />
          <div className="h-3 bg-gray-100 rounded w-16" />
        </div>
      </td>
      <td className="px-6 py-5">
        <div className="h-6 bg-gray-200 rounded-full w-20" />
      </td>
      {colSpan > 3 && (
        <td className="px-6 py-5">
          <div className="h-6 bg-gray-200 rounded-full w-18" />
        </td>
      )}
      {colSpan > 4 && (
        <td className="px-6 py-5">
          <div className="h-4 bg-gray-200 rounded w-20" />
        </td>
      )}
    </tr>
  );
}

// ============================================================================
// Table Row Component
// ============================================================================

interface EncounterRowProps {
  item: UnifiedEncounterItem;
  showMatchStatus: boolean;
  showExternalSystem: boolean;
  showProvider: boolean;
  onViewDetails?: (item: UnifiedEncounterItem) => void;
  onChangeProvider?: (item: UnifiedEncounterItem) => void;
  isEven: boolean;
}

function EncounterRow({
  item,
  showMatchStatus,
  showExternalSystem,
  showProvider,
  onViewDetails,
  onChangeProvider,
  isEven,
}: EncounterRowProps) {
  const handleRowClick = () => {
    if (onViewDetails) {
      onViewDetails(item);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && onViewDetails) {
      e.preventDefault();
      onViewDetails(item);
    }
  };

  return (
    <tr
      className={`group ${isEven ? 'bg-gray-50/40' : 'bg-white'} hover:bg-blue-50/50 transition-colors cursor-pointer`}
      onClick={handleRowClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View details for ${item.patientName}`}
    >
      {/* Patient & Date */}
      <td className="px-6 py-5 whitespace-nowrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">{item.patientName}</span>
            {item.patientMrn && (
              <span className="text-[11px] font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                {item.patientMrn}
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-sm">
            {item.externalSystem && EHR_PRE_LOCALIZED_SYSTEMS.has(item.externalSystem) ? (
              <>
                <span className="font-medium text-gray-700">{formatEhrTime(item.scheduledDate)}</span>
                <span className="text-gray-300">&middot;</span>
                <span className="text-gray-500">{formatEhrDate(item.scheduledDate)}</span>
              </>
            ) : (
              <>
                <span className="font-medium text-gray-700">{format(item.scheduledDate, 'h:mm a')}</span>
                <span className="text-gray-300">&middot;</span>
                <span className="text-gray-500">{format(item.scheduledDate, 'MMM d, yyyy')}</span>
              </>
            )}
          </div>
        </div>
      </td>

      {/* Type & Location */}
      <td className="px-6 py-5 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-gray-900 capitalize">{item.type}</div>
          {item.location && <div className="text-sm text-gray-500 mt-0.5">{item.location}</div>}
        </div>
      </td>

      {/* Status */}
      <td className="px-6 py-5 whitespace-nowrap">
        {/* Transcribing only stands in for a status before the note; never for a delivery outcome or a cancellation. */}
        <StatusBadge
          status={item.isTranscribing && (item.status === 'in-progress' || item.status === 'scheduled') ? 'transcribing' : item.status}
          type="encounter"
        />
      </td>

      {/* Provider (standalone) */}
      {showProvider && (
        <td className="px-6 py-5 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">{item.providerName || 'Unassigned'}</span>
            {onChangeProvider && item.recordingStatus === 'pending' && (item.transcriptionStatus ?? 'pending') === 'pending' && (
              <button
                type="button"
                className="text-xs font-medium text-blue-700 hover:text-blue-900 underline-offset-2 hover:underline"
                aria-label={`Change provider for ${item.patientName}`}
                onClick={(e) => { e.stopPropagation(); onChangeProvider(item); }}
                onKeyDown={(e) => e.stopPropagation()}
              >
                Change
              </button>
            )}
          </div>
        </td>
      )}

      {/* Match Status (Connected Mode) */}
      {showMatchStatus && (
        <td className="px-6 py-5 whitespace-nowrap">
          {item.matchStatus && <StatusBadge status={item.matchStatus} type="match" />}
        </td>
      )}

      {/* External System (Connected Mode) */}
      {showExternalSystem && (
        <td className="px-6 py-5 whitespace-nowrap">
          {item.externalSystem && (
            <div className="text-xs text-gray-500">
              <div className="uppercase font-semibold tracking-wide text-gray-600">{item.externalSystem}</div>
              {item.externalId && (
                <div className="font-mono truncate max-w-[120px] mt-0.5" title={item.externalId}>
                  {item.externalId}
                </div>
              )}
            </div>
          )}
        </td>
      )}
    </tr>
  );
}

// ============================================================================
// Main Table Component
// ============================================================================

export function EncounterTable() {
  const { items, loading, config, refresh } = useEncounterList();
  const showProvider = config.mode === 'standalone';
  const changeProvider = config.onChangeProvider
    ? (item: UnifiedEncounterItem) => config.onChangeProvider!(item, refresh)
    : undefined;

  const showMatchStatus = config.mode === 'connected' && config.showMatchStatus !== false;
  const showExternalSystem = config.mode === 'connected' && config.showExternalSystem !== false;

  let colSpan = showProvider ? 4 : 3;
  if (showMatchStatus) colSpan++;
  if (showExternalSystem) colSpan++;

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-clinical-soft">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr className="bg-gray-50/80">
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Patient & Date
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Type & Location
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Status
            </th>
            {showProvider && (
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Provider
              </th>
            )}
            {showMatchStatus && (
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Match Status
              </th>
            )}
            {showExternalSystem && (
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Source
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading && items.length === 0 ? (
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} colSpan={colSpan} />
            ))
          ) : (
            items.map((item, index) => (
              <EncounterRow
                key={item.id}
                item={item}
                showMatchStatus={showMatchStatus}
                showExternalSystem={showExternalSystem}
                showProvider={showProvider}
                onViewDetails={config.onViewDetails || config.onItemClick}
                onChangeProvider={changeProvider}
                isEven={index % 2 === 1}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default EncounterTable;
